<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="full-width">
    <!--Card for list view-->
    <template v-if="isListView">
      <div
        class="card column"
        :class="dark && 'bg-dark text-white'"
        @click="mentionClicked(mention)"
      >
        <div
          class="mention-content list-content row no-wrap relative items-center"
        >
          <QCheckbox
            v-show="showCheckbox"
            v-model="mention.selected"
            class="q-pb-md col-auto"
            size="27px"
            @update:model-value="$emit('mention-selected', mention)"
          />

          <div class="column col q-ml-sm">
            <slot name="list-title">
              <span class="row fit justify-between">
                <span style="max-width: 90%">
                  <strong>{{ listTitle || title }}</strong>
                  <QIcon
                    v-if="mention.author_verified"
                    class="q-ma-xs q-mb-sm"
                    color="black"
                    name="ion-checkmark-circle"
                    size="13px"
                    style="height: 0 !important"
                  />
                </span>
                <span class="row q-mr-lg q-mb-none q-pa-none actions">
                  <SentimentWidget
                    :mention="mention"
                    :stream="stream"
                  />
                </span>
              </span>
              <p class="q-pl-xs q-pt-xs softer">
                {{
                  formatIntlDate(mention.timestamp, {
                    day: "numeric",
                    month: "short",
                    hour: "numeric",
                    minute: "numeric",
                  })
                }}
              </p>
            </slot>
            <slot name="list-body">
              <MentionExcerpts
                v-if="mention.excerpts"
                :line-clamp="10"
                list-view
                :mention="mention"
                :no-highlighting="noHighlighting"
              />
            </slot>
            <BookmarkLabels
              v-if="bookmarkTags.length"
              :bookmarks="bookmarkTags"
              :mention="mention"
              @tag-deleted="deleteTag"
            />
            <slot name="list-body-append" />
            <Component
              :is="actionSheetComponent"
              class="mention-dropdown"
              :mention="mention"
              :mentions="mentions"
              :origin="origin"
              :stream="stream"
              @mention-removed="removeMention"
              @tags-changed-in-action-sheet="updateTags"
            />
          </div>
        </div>
      </div>
    </template>

    <!--Card for streams-->
    <template v-else>
      <div
        class="card social-stream-view-card column"
        :class="[
          contentClass,
          dark && 'bg-dark text-white',
          hideBorderTop && 'hide-radius-top',
          hideBorderBottom && 'hide-radius-bottom',
        ]"
        @click="mentionClicked(mention)"
      >
        <slot name="before" />

        <div
          class="mention-content stream-content column no-wrap fit relative"
          :class="[
            hideBorderTop && 'hide-radius-top',
            hideBorderBottom && 'hide-radius-bottom',
          ]"
        >
          <div class="row no-wrap">
            <QCheckbox
              v-show="showCheckbox"
              v-model="mention.selected"
              class="q-pr-xs items-baseline"
              size="xs"
              @update:model-value="$emit('mention-selected', mention)"
            />
            <div class="full-width">
              <div class="row no-wrap justify-between">
                <div class="column">
                  <strong
                    v-if="title"
                    class="q-mb-sm bigger2 e2e-mention-title ellipsis-2-lines block"
                  >
                    <slot name="innerTitle">
                      {{ title }}
                    </slot>
                  </strong>
                  <div class="row no-wrap q-mb-md items-start justify-start">
                    <!--On mobile, we display logo with the content to save space-->
                    <QIcon
                      v-if="icon"
                      class="logo"
                      :name="icon"
                      size="20px"
                    />
                    <div class="column col e2e-mention-details flex-basis-100">
                      <slot name="header" />
                    </div>
                  </div>
                </div>

                <div class="actions column items-center">
                  <Component
                    :is="actionSheetComponent"
                    button-color="blue-grey"
                    :mention="mention"
                    :mentions="mentions"
                    :origin="origin"
                    :stream="stream"
                    @mention-removed="removeMention"
                    @tags-changed-in-action-sheet="updateTags"
                  />
                </div>
              </div>

              <slot name="beforeBody" />
              <slot name="body">
                <slot name="beforeExcerpts" />

                <MentionExcerpts
                  v-if="mention.excerpts"
                  expandable
                  :line-clamp="10"
                  :mention="mention"
                  :no-highlighting="noHighlighting"
                  social-stream
                />

                <slot name="afterExcerpts" />
              </slot>

              <BookmarkLabels
                v-if="bookmarkTags.length"
                :bookmarks="bookmarkTags"
                :mention="mention"
                @tag-deleted="deleteTag"
              />

              <template v-if="!hideMedia">
                <div
                  class="attachments-container q-gutter-x-xs justify-end"
                  :class="hasAttachmentsSlot && 'row q-mt-sm'"
                  @click.stop.prevent
                >
                  <slot name="attachments" />
                </div>
              </template>

              <template v-if="!hideEngagement">
                <div class="row q-mt-sm engagement items-baseline justify-end">
                  <slot name="engagement-items" />
                  <SentimentWidget
                    v-if="!hideSentiment"
                    dense
                    :mention="mention"
                    :stream="stream"
                  />
                </div>
              </template>

              <slot
                v-if="!options.hideAfterBodySlot"
                name="afterBody"
              />
              <slot />
            </div>
          </div>
          <slot name="afterRow" />
        </div>
      </div>

      <slot name="after" />
    </template>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";

import BookmarkLabels from "shared/components/BookmarkLabels.vue";
import MentionExcerpts from "shared/components/MentionExcerpts.vue";
import SentimentWidget from "shared/components/SentimentWidget.vue";
import SocialMentionEngagementSentiment from "shared/components/SocialMentionEngagementSentiment.vue";
import useMention from "shared/composables/useMention";
import useTeamPicker from "shared/composables/useTeamPicker";
import { formatIntlDate } from "shared/helpers/date";
import { mentionKeywords } from "shared/helpers/mentions";
import { useMentionCountsStore } from "shared/stores/mentionCounts";

import { props } from "./MentionCardProps";

export default {
  name: "SocialMentionCard",
  components: {
    BookmarkLabels,
    SentimentWidget,
    MentionExcerpts,
    SocialMentionEngagementSentiment,
  },
  props,
  emits: ["click", "mention-selected", "mention-removed"],
  setup(mentionProps, context) {
    const { mentionClicked } = useMention(mentionProps, context);

    const mentionCountsStore = useMentionCountsStore();
    const { mentionCount } = storeToRefs(mentionCountsStore);

    const { selectedTeamPreference } = useTeamPicker();

    return {
      mentionClicked,
      mentionCount,
      selectedTeamPreference,
    };
  },
  data() {
    return {
      bookmarkTags: [],
    };
  },
  computed: {
    hasAttachmentsSlot() {
      return Boolean(this.$slots.attachments);
    },
    hideBorderTop() {
      return (
        !this.options.noSquareTopBorder &&
        this.mentions[0] === this.mention &&
        Boolean(this.mentionCount(this.stream.id))
      );
    },
    hideBorderBottom() {
      return (
        Boolean(this.mention.related_article) ||
        (this.mention.similarMentions && this.mention.similarMentions.length)
      );
    },
    isListView() {
      return this.options && this.options.list;
    },
    origin() {
      const originKeyword = mentionKeywords(this.mention)[0] || "";

      if (this.stream.id) {
        return {
          origin_id: this.stream.id,
          origin_type: "SocialStream",
          origin_keyword: originKeyword,
        };
      }

      if (this.stream.folderId) {
        return {
          origin_id: this.stream.folderId,
          origin_type: "Folder",
          origin_keyword: originKeyword,
        };
      }

      return {};
    },
  },
  mounted() {
    this.resetBookmarks();
  },
  methods: {
    formatIntlDate,
    resetBookmarks() {
      if (this.$features.has("has_team_workspaces")) {
        this.bookmarkTags = (this.mention.bookmarks || []).filter(
          (bookmark) =>
            bookmark.organisation_team_id === this.selectedTeamPreference ||
            !bookmark.organisation_team_id
        );
      } else {
        this.bookmarkTags = this.mention.bookmarks || [];
      }
    },
    removeMention(mention) {
      this.$emit("mention-removed", mention);
    },
    updateTags(bookmarks) {
      this.bookmarkTags = bookmarks;
    },
    deleteTag(tagId) {
      this.bookmarkTags = this.bookmarkTags.filter((tag) => tag.id !== tagId);
    },
  },
};
</script>

<style lang="scss" scoped>
.actions {
  margin-top: -8px;
  margin-right: -8px;

  :deep(.q-btn__wrapper) {
    min-width: 0 !important;
    min-height: 0 !important;
  }

  :deep(.play-button) {
    height: auto;
  }
}

.q-checkbox {
  :deep(.q-checkbox__bg) {
    border: 1px solid #3e475e;
  }
}

.e2e-mention-title {
  word-break: break-word;
  letter-spacing: 0.02em;
}

:deep(.muted) {
  font-size: 80%;
  line-height: 100%;
  opacity: 0.5;
  color: #444;
  font-weight: normal;
}

:deep(.pull-right) {
  float: right;
}

:deep(.publishers-list) {
  background-color: #eee;
  padding: 5px;
  margin-top: 5px;
  margin-bottom: 5px;

  a {
    width: 100%;
    display: inline-block;
  }
}

:deep(.mention-content) {
  &.stream-content {
    border-radius: 4px;
    padding: 9px 15px;
  }

  &.list-content {
    padding: 10px 20px 10px 15px;
  }
}

:deep(.syndication-time) {
  margin-top: 5px;
  margin-left: auto;
  margin-right: 5px;
  white-space: nowrap;
  padding-left: 5px;
}

:deep(.card) {
  border-radius: 4px;

  &.syndication-card {
    margin-top: 2px;
    padding-left: 20px;
  }

  p {
    margin-bottom: 5px;
    line-height: 20px;
  }

  p em {
    background-color: rgb(252 220 89 / 30%);
    padding: 1px 2px;
    font-style: normal;
  }

  .logo {
    margin-right: 8px;
  }

  &.warning {
    h3 {
      margin: 12px 0;
    }

    .q-icon {
      vertical-align: -3px;
    }
  }

  .ion-checkmark-circle {
    color: rgb(73 208 103 / 50%);
  }

  .ion-close-circle {
    color: rgb(249 88 88 / 50%);
  }

  .ion-remove-circle {
    color: rgb(50 159 236 / 50%);
  }
}

:deep(.q-item-label) {
  color: yellow;
}

.youtube {
  :deep(.logo.q-icon) {
    height: auto;
  }
}

:deep(.title) {
  line-height: 18px;
  margin-top: 1px;
  word-break: break-word;
}

:deep(.subtitle) {
  font-weight: normal;
  font-size: 13px;
  line-height: 15px;
}

.engagement {
  span {
    margin-right: 13px;

    &:last-child {
      margin-right: 0;
    }
  }
}
</style>
